import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { useCustomBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  boundaryTerritoryGroupsCreateError, boundaryTerritoryGroupsCreateRequest, boundaryTerritoryGroupsFetchRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { getBoundaryTerritoryGroups } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { translate } from '~/translations/Trans';
import { type BoundaryFillType } from '../../../../../../boundary/settings/boundaryFill.type';
import {
  createBoundaryTerritoryGroupRequestParams, getCalculateBucketFunction,
} from '../../boundaryTerritoryGroup.helpers';
import { useBoundaryGroupUsage } from '../../hooks/useBoundaryGroupUsage';
import {
  type LoadCustomBoundaryGroupData,
  LoadCustomBoundaryGroupPaneComponent,
} from './loadCustomBoundaryGroupPane.component';
import { LoadCustomBoundaryFillType } from './loadCustomBoundaryGroupPane.helpers';

export type LoadCustomBoundaryGroupPaneContainerProps = Readonly<{
  resetForm: () => void;
}>;

export const LoadCustomBoundaryGroupPaneContainer: FC<LoadCustomBoundaryGroupPaneContainerProps> = props => {
  const { isBoundaryGroupUsedOnMap } = useBoundaryGroupUsage();
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const dispatch = useDispatch();

  const handleAddBoundary = async (data: LoadCustomBoundaryGroupData) => {
    const territoryGroup = customBoundaryGroups.find(item => item.id === data.boundaryGroupId);

    if ((mapId === null) || (clientId === null) || !territoryGroup) {
      return;
    }

    const boundaryFillType: BoundaryFillType =
      data.boundaryFillType === LoadCustomBoundaryFillType.FillBasedOnData
        ? LoadCustomBoundaryFillType.DefaultFill :
        data.boundaryFillType;

    let boundaryTerritoryType = BoundaryTerritoryType.Manual;
    let calculateBucketFunction = getCalculateBucketFunction(BoundaryTerritoryType.Manual);
    let boundaryFillData = null;

    if (data.boundaryFillType === LoadCustomBoundaryFillType.FillBasedOnData) {
      boundaryTerritoryType = data.boundaryTerritoryType;
      calculateBucketFunction = getCalculateBucketFunction(data.boundaryTerritoryType, data.numericCalculateBucketFunction);
      boundaryFillData = data.boundaryFillData ?? null;
    }

    const { boundary_territory_groups: serverBTGs } = await getBoundaryTerritoryGroups(clientId, { map_id: mapId });
    // BTG was potentially already added to the map
    const lookedUpGroup = serverBTGs.find(btg => btg.boundary_group_id === data.boundaryGroupId);

    // check if it was already added
    if (lookedUpGroup) {
      dispatch(boundaryTerritoryGroupsCreateError());

      dispatch(createAppError({
        type: AppErrorType.General,
        title: translate('Territory group was already added'),
        errorTitle: translate('Territory group was already added to the map'),
      }));

      dispatch(boundaryTerritoryGroupsFetchRequest(mapId));

      return;
    }

    const btgCreateRequest = createBoundaryTerritoryGroupRequestParams({
      mapId,
      boundaryGroupId: territoryGroup.id,
      territoryType: boundaryTerritoryType,
      numericCalculateBucketFunction: calculateBucketFunction,
      fillData: boundaryFillData,
    });

    dispatch(boundaryTerritoryGroupsCreateRequest({
      ...btgCreateRequest,
      settings: {
        ...btgCreateRequest.settings,
        boundary_fill_type: boundaryFillType,
      },
    }));

    props.resetForm();
  };

  return (
    <LoadCustomBoundaryGroupPaneComponent
      customBoundaryGroups={customBoundaryGroups}
      isBoundaryGroupUsedOnMap={isBoundaryGroupUsedOnMap}
      onAddBoundary={handleAddBoundary}
    />
  );
};
